import PropTypes from 'prop-types';
import { useState, useMemo, useEffect } from 'react';
import { TableProvider, useTableProvider } from 'contexts/TableProvider';
import { useBee } from 'contexts/Bee';
import { useFetch } from "use-http";
import { useDispatch } from 'react-redux';
import moment from 'moment';
import PrivateElement from './PrivateElement';

// material-ui
import
{
   Alert,
   AlertTitle,
   CardContent,
   Stack,
   TablePagination,
   Typography,
   TableFooter,
   TableRow,
   TableCell
} from '@mui/material';

//project import
import { openSnackbar } from 'store/reducers/snackbar';

//Components
import MainCard from 'components/MainCard';
import TableFilters from 'components/TableFilters';
import SkeletonTable from 'components/SkeletonTable';
import ResourceTable from 'components/ResourceTable';
import TablePaginationActions from 'components/TablePaginationActions';

/**
 * Generates the markup for the table footer.
 *
 * @param {Object} props - The props passed to the component.
 * @return {JSX.Element} The JSX element representing the table footer markup.
 */
const TableFooterMarkup = (props) =>
{
   return (
      <Stack
         direction="row"
      >
         <TablePagination
            {...props}
         />
      </Stack>
   )
}

const LogTableMarkup = (props) =>
{
   const {
      title = 'Logs',
      logs
   } = props;

   const {
      reload,
   } = useBee();

   console.log('RELOAD', reload);

   const {
      page,
      rowsPerPage,
      sortOrder,
      totalCount,
      onPageChange,
      onRowsPerPageChange,
      onSortChange,
      groupBy,
      onGroupByChange,
      loading,
      error,
      autoload,
      data
   } = useTableProvider();

   const dispatch = useDispatch();

   //inizializzo l'oggetto per la chiamata fetch
   const fetcher = useFetch(process.env.REACT_APP_BASE_API_URL, { cachePolicy: 'no-cache' });

   const [updating, setUpdating] = useState(null);
   const [success, setSuccess] = useState(null);

   useEffect(() => 
   {
      if (success !== null)
      {
         dispatch(
            openSnackbar({
               anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
               },
               open: true,
               message: success?.message,
               variant: 'alert',
               alert: {
                  color: 'success'
               },
               close: false
            })
         );
      }

      return () =>
      {
         setSuccess(null);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [success]);

   useEffect(() =>
   {

      if (!loading && updating)
      {
         setUpdating(null);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loading]);

   //gestisco gli errori dell'oggetto fetcher
   useEffect(() => 
   {
      if (fetcher.error)
      {
         dispatch(
            openSnackbar({
               anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
               },
               open: true,
               message: fetcher.error?.message,
               variant: 'alert',
               alert: {
                  color: 'error'
               },
               close: false
            })
         );
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fetcher.error]);

   const filters = useMemo(() =>
   {
      const fields = [
         {
            type: 'globalSearch',
            label: 'Ricerca',
            visible: true,
            name: 'global',
            id: 'globalsearch',
            sizes: {
               lg: 3
            }
         }
      ];

      if (logs.length > 0)
      {
         fields.push({
            type: 'select',
            label: 'Tipo di Log',
            visible: true,
            name: 'content_type',
            id: 'content_type',
            blank: false,
            options: logs,
         });
      }
      return {
         fields: fields
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [logs]);

   //creo l'oggetto columns per gestire le colonne della tabella
   const columns = useMemo(
      () => [
         {
            Header: 'Azione',
            accessor: 'azione',
            draggable: false,
            sortable: true,
            sx: {
               verticalAlign: 'top'
            }
         },
         {
            Header: 'Data',
            accessor: 'data_ora',
            draggable: false,
            sx: {
               verticalAlign: 'top'
            },
            Cell: ({ value }) =>
            {
               return moment(value, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm:ss');
            }
         },
         {
            Header: 'Operatore',
            accessor: 'utente',
            draggable: false,
            sortable: true,
            sx: {
               verticalAlign: 'top'
            },
            Cell: ({ value }) =>
            {
               return `${value?.first_name} ${value?.last_name}`
            }
         },
         {
            Header: 'Note',
            accessor: 'note',
            draggable: false,
            sx: {
               verticalAlign: 'top'
            }
         },
         {
            Header: 'Changelog',
            accessor: 'dati.change_log',
            draggable: false,
            sx: {
               verticalAlign: 'top'
            },
            Cell: ({ value }) =>
            {
               return value ? value.map((el, index) => <Typography variant="body2" sx={{ m: 0 }} key={index}>{el}</Typography>) : '';
            }
         }
      ],
      // eslint-disable-next-line
      []
   );
   //imposto l'ordine iniziale delle colonne della tabella
   const initialState = useMemo(
      () => ({
         columnOrder: [
            'azione',
            'data_ora',
            'utente',
            'note',
            'dati.change_log'
         ],
      }),
      []
   );

   const renderContent = () =>
   {
      if (error)
      {
         return (
            <Alert
               severity="error"
            >
               <AlertTitle
                  sx={{
                     fontWeight: 700
                  }}
               >
                  Errore server
               </AlertTitle>
               {`Si è verificato un problema durante il recupero dei dati (Error: ${error})`}
            </Alert>
         )
      } else if (!autoload)
      {
         return (
            <Alert
               severity="info"
            >
               <AlertTitle
                  sx={{
                     fontWeight: 700
                  }}
               >
                  Attenzione
               </AlertTitle>
               Inizia impostando alcuni filtri per poter visualizzare risultati
            </Alert>
         )
      } else if (loading && !updating)
      {
         return (
            <SkeletonTable rows={5} columns={6} />
         )
      } else if (data.results)
      {
         return (
            <ResourceTable
               columns={columns}
               data={data}
               initialState={initialState}
               orderAsc={sortOrder}
               onSortChange={onSortChange}
               groupByField={groupBy}
               onGroupByChange={onGroupByChange}
               footer={
                  <TableFooter>
                     <TableRow>
                        <TableCell colSpan={columns?.length}>
                           <TableFooterMarkup
                              page={!totalCount || totalCount <= 0 ? 0 : page}
                              count={totalCount}
                              onPageChange={onPageChange}
                              rowsPerPageOptions={[10, 25, 50, 100]}
                              component='div'
                              rowsPerPage={rowsPerPage}
                              onRowsPerPageChange={onRowsPerPageChange}
                              labelRowsPerPage='Risultati per pagina'
                              labelDisplayedRows={({ from, to, count }) =>
                              {
                                 return `${from}–${to} di ${count !== -1 ? count : `altre ${to}`}`;
                              }}
                              SelectProps={{
                                 name: "page_size"
                              }}
                              ActionsComponent={TablePaginationActions}
                              sx={{
                                 '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    marginTop: 0,
                                    marginBottom: 0
                                 },
                                 '& .MuiToolbar-root': {
                                    paddingLeft: 0
                                 }
                              }}
                           />
                        </TableCell>
                     </TableRow>
                  </TableFooter>

               }
            />
         )
      }
   }

   return (
      <>
         <MainCard
            content={false}
            title={title}
         >
            <Stack
               sx={{
                  px: 2.75,
                  pt: 2.75
               }}
            >
               <TableFilters
                  filters={filters}
                  disable={loading}
                  filtersToggler={false}
               />
            </Stack>
            <CardContent
               sx={{
                  px: 0,
                  '&:last-child': {
                     pb: 0
                  }
               }}
            >
               {renderContent()}
            </CardContent>
         </MainCard>
      </>
   )

}

LogTableMarkup.propTypes = {
   title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
   logs: PropTypes.array,
   value: PropTypes.any,
   row: PropTypes.object
};

/**
 * Renders the `LogTableMarkup` component.
 *
 * @return {JSX.Element} The rendered `LogTableMarkup` component.
 */
const LogTable = (props) =>
{
   const {
      title,
      contentType,
      objectId = null,
      contrattoId = null,
      onlyRelated = null,
      showRelated = null,
      logs = []
   } = props;

   const defaultFilters = {
      content_type: contentType,
   }

   if (objectId)
   {
      defaultFilters.object_id = objectId;
   }

   if (contrattoId)
   {
      defaultFilters.contratto_id = contrattoId;
   }

   if (onlyRelated)
   {
      defaultFilters.only_related = true;
   }

   if (showRelated)
   {
      defaultFilters.show_related = true;
   }

   return (
      <PrivateElement capabilities={['superadmin', 'admin', 'erp_admin']}>
         <TableProvider
            path={'/api/log/list/'}
            defaultCustomQuery={defaultFilters}
         >
            <LogTableMarkup title={title} logs={logs} />
         </TableProvider>
      </PrivateElement>
   )
}

LogTable.propTypes = {
   title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
   contentType: PropTypes.string,
   logs: PropTypes.array,
   objectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   contrattoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onlyRelated: PropTypes.bool,
   showRelated: PropTypes.bool
};

export default LogTable;